import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { AppShell } from "@/App"
import {
  listOrders,
  listOrderItems,
  listEticdataDocNumbers,
  realEncNumber,
  resolvePaymentInfo,
  type OrderRow,
  type OrderItem,
} from "@/lib/api"
import { useOrderSources } from "@/lib/sources"
import { SourceBadge } from "@/components/source-badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Boxes, RefreshCw, Search, X, PackageCheck, ArrowLeft } from "lucide-react"

interface LogisticaProps {
  shell: AppShell | null
}

type Tab = "separar" | "pesquisa"

/** A line is separated when separated_at is set. */
const isSep = (it: OrderItem) => it.separated_at != null

export function Logistica({ shell }: LogisticaProps) {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [items, setItems] = useState<OrderItem[]>([])
  const [encByOrder, setEncByOrder] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>("separar")
  const [sourceFilter, setSourceFilter] = useState<string>("all")
  const sources = useOrderSources(shell)
  const [query, setQuery] = useState("")
  /** Pesquisa na lista "A separar": nº da encomenda (ENC/online/balcão) ou nome do cliente. */
  const [orderQuery, setOrderQuery] = useState("")
  const [selectedSku, setSelectedSku] = useState<string | null>(null)

  async function load() {
    if (!shell) return
    try {
      const [o, enc] = await Promise.all([
        listOrders(shell, 100),
        listEticdataDocNumbers(shell),
      ])
      // Fetch items per OPEN order (server-filtered) — avoids the capped
      // global fetch dropping rows once order_items grows. ≤~20 open orders.
      const open = o.filter((x) => !x.completed_at && !x.cancelled_at)
      const itemArrays = await Promise.all(open.map((oo) => listOrderItems(shell, oo.id)))
      setOrders(o)
      setItems(itemArrays.flat())
      setEncByOrder(enc)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), 8000)
    return () => clearInterval(t)
  }, [shell])

  // ── Derived indexes ────────────────────────────────────────────────────
  // Canceladas no Shopify saem da logística (o armazém é avisado por email).
  const openOrders = useMemo(() => orders.filter((o) => !o.completed_at && !o.cancelled_at), [orders])
  const openIds = useMemo(() => new Set(openOrders.map((o) => o.id)), [openOrders])
  const orderById = useMemo(() => new Map(orders.map((o) => [o.id, o])), [orders])

  const itemsByOrder = useMemo(() => {
    const m = new Map<string, OrderItem[]>()
    for (const it of items) {
      const arr = m.get(it.order_id) ?? []
      arr.push(it)
      m.set(it.order_id, arr)
    }
    return m
  }, [items])

  // Orders awaiting logistics validation (logistica = current).
  const bySource = (o: OrderRow) => sourceFilter === "all" || sources.sectionOf(o) === sourceFilter
  const toSeparate = useMemo(
    () => openOrders.filter((o) => o.step_states?.logistica === "current" && bySource(o)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openOrders, sourceFilter, sources],
  )

  // Levantamento em loja já separado, à espera que o cliente levante — o
  // armazém regista aqui "Marcar como levantada".
  const awaitingPickup = useMemo(
    () =>
      openOrders.filter(
        (o) =>
          o.step_states?.logistica === "done" &&
          !o.picked_up_at &&
          resolvePaymentInfo(o).pickupInStore &&
          bySource(o),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openOrders, sourceFilter, sources],
  )

  // Canceladas ou alteradas pelo cliente nos últimos 3 dias que já tinham
  // chegado ao armazém (canceladas) ou que saíram da separação à espera da
  // lista nova (alteradas): o armazém vê o aviso aqui, não só no email.
  const changedRecently = useMemo(() => {
    const since = Date.now() - 3 * 86_400_000
    const recent = (ts?: string | null) => !!ts && new Date(ts).getTime() >= since
    return orders.filter((o) => {
      if (!bySource(o)) return false
      if (o.cancelled_at) return recent(o.cancelled_at) && !!o.step_states?.logistica
      return (
        !o.completed_at &&
        !!o.change_alert &&
        recent(o.shopify_changed_at) &&
        (o.step_states?.logistica !== "current" || !!o.payment_pending_at)
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, sourceFilter, sources])

  // Reverse index over OPEN-order items: sku → { name, orderIds }.
  const skuIndex = useMemo(() => {
    const m = new Map<string, { name: string; orderIds: Set<string> }>()
    for (const it of items) {
      if (!openIds.has(it.order_id)) continue
      const key = it.sku.trim()
      if (!key) continue
      const entry = m.get(key) ?? { name: it.name ?? key, orderIds: new Set<string>() }
      if (!entry.name && it.name) entry.name = it.name
      entry.orderIds.add(it.order_id)
      m.set(key, entry)
    }
    return m
  }, [items, openIds])

  const q = query.trim().toLowerCase()
  const suggestions = useMemo(() => {
    if (!q) return []
    return Array.from(skuIndex.entries())
      .filter(([sku, e]) => sku.toLowerCase().includes(q) || e.name.toLowerCase().includes(q))
      .slice(0, 12)
      .map(([sku, e]) => ({ sku, name: e.name, count: e.orderIds.size }))
  }, [q, skuIndex])

  // Orders matching the selected SKU (open only).
  const searchResults = useMemo(() => {
    if (!selectedSku) return []
    const entry = skuIndex.get(selectedSku)
    if (!entry) return []
    return Array.from(entry.orderIds)
      .map((id) => orderById.get(id))
      .filter((o): o is OrderRow => o != null)
  }, [selectedSku, skuIndex, orderById])

  /** Progresso de separação AGREGADO por SKU — mesmo critério do detalhe (e da
   *  ENC no ETICDATA). Uma linha só conta como separada quando TODAS as
   *  order_items desse SKU estão picadas. */
  function sepProgress(orderId: string): { sep: number; total: number; partial: boolean } {
    const arr = itemsByOrder.get(orderId) ?? []
    const bySku = new Map<string, boolean>()
    for (const it of arr) {
      const key = `${it.sku.trim().toLowerCase()}|${String(it.name ?? "").trim().toLowerCase()}`
      const prev = bySku.get(key)
      bySku.set(key, prev === undefined ? isSep(it) : prev && isSep(it))
    }
    const total = bySku.size
    const sep = Array.from(bySku.values()).filter(Boolean).length
    return { sep, total, partial: sep > 0 && sep < total }
  }

  // Sem acentos e minúsculas: "joão" encontra "JOAO".
  const fold = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  const shownToSeparate = useMemo(() => {
    const q = fold(orderQuery.trim())
    if (!q) return toSeparate
    return toSeparate.filter((o) =>
      [orderLabel(o), o.shopify_order_number, o.ship_name, o.customer_name, o.bill_name]
        .some((v) => v != null && fold(String(v)).includes(q)),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toSeparate, orderQuery, encByOrder])

  // Primary identifier = ETICDATA ENC number; fall back to the Shopify number.
  function orderLabel(o: OrderRow): string {
    // Nº ENC do ETICDATA quando existe; a nossa ref sintética (KTX-…) não é um
    // nº ENC, por isso cai no nº da encomenda online.
    return realEncNumber(encByOrder.get(o.id) ?? null) ?? o.shopify_order_number ?? o.id.slice(0, 8)
  }

  if (!shell) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Standalone dev (sem token). Abre via o host para ver dados.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          {shell.role >= 5 && (
            <Button variant="outline" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="mr-1.5 size-3.5" /> Voltar
            </Button>
          )}
          <Boxes className="size-5 text-muted-foreground" />
          <div>
            <h1 className="text-lg font-semibold leading-tight">Logística — separação</h1>
            <p className="text-xs text-muted-foreground">{toSeparate.length} a separar</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="h-8 rounded-md border bg-background px-2 text-sm"
            title="Filtrar por fonte"
          >
            <option value="all">Todas as fontes</option>
            {sources.options.map((s) => (
              <option key={s.section} value={s.section}>
                {s.name}
              </option>
            ))}
          </select>
          <RefreshCw className="size-3.5 animate-spin-slow" />
          <span>Refresh 8s</span>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {/* Tabs + pesquisa da lista "A separar" */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border p-0.5">
          <button
            type="button"
            onClick={() => setTab("separar")}
            className={
              "rounded px-3 py-1 text-sm font-medium transition-colors " +
              (tab === "separar" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent")
            }
          >
            A separar ({toSeparate.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("pesquisa")}
            className={
              "rounded px-3 py-1 text-sm font-medium transition-colors " +
              (tab === "pesquisa" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent")
            }
          >
            Pesquisa por produto
          </button>
        </div>
        {tab === "separar" && (
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Nº da encomenda ou cliente…"
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              className="h-9 pl-8 pr-8"
            />
            {orderQuery && (
              <button
                type="button"
                onClick={() => setOrderQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent"
                aria-label="Limpar"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        )}
        </div>

        {error && (
          <Card className="mb-4 border-destructive/30 bg-destructive/5">
            <CardContent className="py-3 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {/* ── A separar ──────────────────────────────────────────────── */}
        {tab === "separar" && (
          <>
            {loading && orders.length === 0 && (
              <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">A carregar…</CardContent></Card>
            )}
            {!loading && toSeparate.length === 0 && (
              <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Nada para separar.</CardContent></Card>
            )}
            {toSeparate.length > 0 && shownToSeparate.length === 0 && (
              <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Nenhuma encomenda corresponde a «{orderQuery.trim()}».</CardContent></Card>
            )}
            {shownToSeparate.length > 0 && (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Entrega</TableHead>
                      <TableHead className="text-right">Separação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shownToSeparate.map((o) => {
                      const p = sepProgress(o.id)
                      const done = p.total > 0 && p.sep === p.total
                      return (
                        <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/logistica/order/${o.id}`)}>
                          <TableCell className="font-mono text-xs">
                            <SourceBadge size="sm" name={sources.sourceName(o)} section={sources.sectionOf(o)} />{" "}
                            {orderLabel(o)}
                          </TableCell>
                          <TableCell className="font-medium">{o.ship_name ?? o.customer_name ?? "—"}</TableCell>
                          <TableCell>
                            {o.ship_city ?? "—"} <span className="text-[11px] text-muted-foreground">{o.ship_zip ?? ""}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-2">
                              {p.partial && (
                                <span className="whitespace-nowrap rounded-md border border-sky-400 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
                                  Parcialmente separada
                                </span>
                              )}
                              <span
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium " +
                                  (done
                                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                                    : "border-amber-400 bg-amber-50 text-amber-800")
                                }
                              >
                                {done && <PackageCheck className="size-3.5" />}
                                {p.sep}/{p.total}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
            {changedRecently.length > 0 && (
              <Card className="mt-4 border-amber-400">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Alteradas / canceladas pelo cliente ({changedRecently.length})</CardTitle>
                </CardHeader>
                <Table>
                  <TableBody>
                    {changedRecently.map((o) => (
                      <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/logistica/order/${o.id}`)}>
                        <TableCell className="font-mono text-xs">{orderLabel(o)}</TableCell>
                        <TableCell className="font-medium">{o.ship_name ?? o.customer_name ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {o.cancelled_at ? (
                            <span className="whitespace-nowrap rounded-md border border-red-500 bg-red-50 px-2 py-0.5 text-[11px] font-bold uppercase text-red-800">
                              Cancelada · repor no stock
                            </span>
                          ) : (
                            <span className="whitespace-nowrap rounded-md border border-amber-500 bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800">
                              Cliente alterou · suspensa
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}

            {awaitingPickup.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Prontas para levantamento ({awaitingPickup.length})</CardTitle>
                </CardHeader>
                <Table>
                  <TableBody>
                    {awaitingPickup.map((o) => (
                      <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/logistica/order/${o.id}`)}>
                        <TableCell className="font-mono text-xs">{orderLabel(o)}</TableCell>
                        <TableCell className="font-medium">{o.ship_name ?? o.customer_name ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          <span className="whitespace-nowrap rounded-md border border-sky-400 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
                            {o.pickup_notified_at ? "Cliente avisado" : "Cliente por avisar"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </>
        )}

        {/* ── Pesquisa por produto (reversa) ────────────────────────── */}
        {tab === "pesquisa" && (
          <div className="space-y-4">
            <div className="relative max-w-xl">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="Referência ou nome do produto…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelectedSku(null) }}
                className="h-10 pl-8 pr-8"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(""); setSelectedSku(null) }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent"
                  aria-label="Limpar"
                >
                  <X className="size-3.5" />
                </button>
              )}
              {/* Autosuggest */}
              {q && !selectedSku && suggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
                  {suggestions.map((s) => (
                    <button
                      key={s.sku}
                      type="button"
                      onClick={() => setSelectedSku(s.sku)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    >
                      <span className="min-w-0">
                        <span className="font-mono text-xs">{s.sku}</span>
                        <span className="ml-2 text-muted-foreground">{s.name}</span>
                      </span>
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {s.count} enc.
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {q && !selectedSku && suggestions.length === 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md">
                  Sem produtos em encomendas abertas para "{query}".
                </div>
              )}
            </div>

            {selectedSku && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    Encomendas abertas com <span className="font-mono">{selectedSku}</span> ({searchResults.length})
                  </CardTitle>
                </CardHeader>
                {searchResults.length === 0 ? (
                  <CardContent className="py-6 text-center text-sm text-muted-foreground">
                    Nenhuma encomenda aberta.
                  </CardContent>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Qtd deste SKU</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {searchResults.map((o) => {
                        const qtyOfSku = (itemsByOrder.get(o.id) ?? [])
                          .filter((it) => it.sku.trim() === selectedSku)
                          .reduce((s, it) => s + (it.qty ?? 0), 0)
                        return (
                          <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/logistica/order/${o.id}`)}>
                            <TableCell className="font-mono text-xs">{orderLabel(o)}</TableCell>
                            <TableCell className="font-medium">{o.ship_name ?? o.customer_name ?? "—"}</TableCell>
                            <TableCell className="text-xs">{o.next_action ?? o.current_step ?? "—"}</TableCell>
                            <TableCell className="text-right font-medium tabular-nums">{qtyOfSku}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
